
import {Options, Vue} from 'vue-class-component'
import {Services} from "@/Services";
import {ExhibitorFilter} from "@/entities/ExhibitorFilter";
import {ExhibitorContainer} from "@/entities/ExhibitorContainer";
import {isDefined, scrollToTop} from "@/utils";
import LazyLoadContainer from "@/components/LazyLoadComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import ExhibitorDetailDialog from "@/components/ExhibitorDetailDialog.vue";
import {Exhibitor} from "@/entities/Exhibitor";
import StandDialog from "@/components/StandDialog.vue";
import Stand from './entities/Stand';
import {zip} from 'rxjs';
import {Observable} from "rxjs/dist/types";
import {ProductContainer} from '@/entities/ProductContainer';
import {Product} from '@/entities/Product';
import ProductDetailDialog from '@/components/ProductDetailDialog.vue';
import {QrcodeCanvas} from "qrcode.vue";
import QrCodeDialog from "@/components/QrCodeDialog.vue";

@Options({
  components: {
    QrCodeDialog,
    QrcodeCanvas,
    ProductDetailDialog, LazyLoadContainer, LoadingSpinner, ExhibitorDetailDialog, StandDialog
  }
})
export default class App extends Vue {

  private productContainer: ProductContainer = null;
  private exhibitorContainer: ExhibitorContainer = null;
  private exhibitorFilter: ExhibitorFilter = new ExhibitorFilter({});
  private searchValue: string = '';
  private pageSize = 20;
  private basePageSize = 20;
  private activeFilters: Record<string, string> = {};
  private isFilterActive: boolean = false;
  private isLoading: boolean = true;
  private activeLetterFilter: string = 'all';

  private selectedExhibitor: Exhibitor;

  constructor(props: any) {
    super(props);

    let loadObservables: Observable<any>[] = [Services.getExhibitorFilters(), Services.getExhibitors()]
    if (process.env.VUE_APP_ENABLE_PRODUCTS === 'true') {
      loadObservables.push(Services.getProducts())
    }

    zip(...loadObservables).subscribe({
      next: value => {
        this.exhibitorFilter = value[0];

        if (process.env.VUE_APP_ENABLE_PRODUCTS === 'true') {
          value[1].exhibitors.forEach(e => {
            let productsForExhibitor = value[2].products.filter(p => p.companyId === e.id);
            if (isDefined(productsForExhibitor) && productsForExhibitor.length > 0) {
              e.products = productsForExhibitor;
            }
          });
          this.productContainer = value[2];
        }
        this.exhibitorContainer = value[1];
        this.isLoading = false;
      },
      error: err => {
        Services.sendErrorMessage(err)
      }
    })
  }

  mounted() {
    window.addEventListener('scroll', this.handleScrollEvent);
  }

  public truncateExhibitorInfo(info: string): string {
    let newInfo = info;
    if (newInfo.includes('<p>') && newInfo.includes('</p>')) {
      const tagStart = newInfo.indexOf('<p>') + 3;
      const tagEnd = newInfo.indexOf('</p>', tagStart);
      if (tagStart > -1 && tagEnd > -1) {
        newInfo = newInfo.substr(tagStart, tagEnd - tagStart);
        return newInfo
      }
    }
    return info;
  }

  private handleScrollEvent(): void {
    if (isDefined(this.$refs.exhibitorList)) {
      //@ts-ignore
      let bottomOfWindow = document.documentElement.scrollTop + window.innerHeight >= this.$refs.exhibitorList.offsetHeight;
      if (bottomOfWindow) {
        this.loadNextPage()
      }
    }
    if (isDefined(this.$refs.scrollToTopButton)) {
      if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "block";
      } else {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "none";
      }
    }
  }

  private scrollTop(): void {
    scrollToTop();
  }

  private onSearchValueInput(event): void {
    if (isDefined(this.exhibitorContainer)) {
      this.resetPageSize();
      this.exhibitorContainer.filterExhibitors(this.searchValue, this.activeFilters, this.activeLetterFilter);
      this.detectChanges();
    }
  }

  private detectChanges(): void {
    this.$nextTick(() => {
      const lazyLoadingContainers = this.$refs.lazyLoadingContainer as LazyLoadContainer[];
      for (const c of lazyLoadingContainers) {
        c.detectChanges();
      }
    });
  }

  private loadNextPage(): void {
    this.pageSize += this.basePageSize;
  }

  private onFilterChanged(event?): void {
    for (const [key, value] of Object.entries(this.activeFilters)) {
      if (value.length === 0) {
        delete this.activeFilters[key];
      }
    }
    if (isDefined(this.exhibitorContainer)) {
      this.resetPageSize();
      this.exhibitorContainer.filterExhibitors(this.searchValue, this.activeFilters, this.activeLetterFilter)
      this.detectChanges();
    }
  }

  private resetPageSize(): void {
    this.pageSize = this.basePageSize;
  }

  private toggleFilter(): void {
    this.isFilterActive = !this.isFilterActive;
    if (!this.isFilterActive) {
      this.resetFilter();
    }
  }

  private resetFilter(): void {
    this.activeFilters = {};
    this.onFilterChanged();
  }

  private hasActiveFilter(): boolean {
    return Object.entries(this.activeFilters).length > 0;
  }

  private openExhibitorDetailDialog(exhibitor: Exhibitor, event: Event): void {
    event.stopImmediatePropagation();
    this.selectedExhibitor = exhibitor;
    const detailDialog: ExhibitorDetailDialog = this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.openDialog(exhibitor);
    }
  }

  private openQrCodeDialog(event: Event): void {
    event.stopImmediatePropagation();
    const qrCodeDialog = this.$refs.qrCodeDialog as QrCodeDialog;
    if (isDefined(qrCodeDialog)) {
      let favoritesString = window.localStorage.getItem('egcf_favorite_exhibitor_list');
      if (!isDefined(favoritesString)) {
        return;
      }
      const qrCodeContent = {
        prj: process.env.VUE_APP_PROJECT,
        exh: JSON.parse(favoritesString)
      }
      qrCodeDialog.openDialog(JSON.stringify(qrCodeContent));
    }
  }

  private closeExhibitorDetailDialog(event: Event): void {
    event.stopImmediatePropagation();
    const detailDialog: ExhibitorDetailDialog = this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.closeDialog();
    }
  }

  private openStandDialog(stand: Stand, event: Event): void {
    event.stopImmediatePropagation();
    if (isDefined(this.$refs.standDialog)) {
      (this.$refs.standDialog as StandDialog).openDialog(stand);
    }
  }

  private onStandDialogClose(): void {
    const detailDialog: ExhibitorDetailDialog = this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog;
    if (isDefined(detailDialog) && detailDialog.isOpen) {
      detailDialog.addClickListener();
    }
  }

  private onProductDialogClose(event: Event): void {
    this.openExhibitorDetailDialog(this.selectedExhibitor, event)
  }

  private openProductDetailDialog(product: Product, event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    this.closeExhibitorDetailDialog(event);
    const detailDialog: ProductDetailDialog = this.$refs.productDetailDialog as ProductDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.openDialog(product);
    }
  }


  private onLetterClicked(letter: string): void {
    this.activeLetterFilter = letter;
    this.onFilterChanged();
  }

  private toggleFavorite(exhibitor: Exhibitor, event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    let favoritesString = window.localStorage.getItem('egcf_favorite_exhibitor_list');
    let favoritesList: String[];
    if (this.isFavorite(exhibitor)) {
      favoritesList = JSON.parse(favoritesString)
      const index = favoritesList.indexOf(exhibitor.id)
      if (index !== -1) {
        favoritesList.splice(index, 1)
      }
    } else {
      if (!isDefined(favoritesString)) {
        favoritesList = [exhibitor.id];
      } else {
        favoritesList = JSON.parse(favoritesString)
        favoritesList.push(exhibitor.id)
      }
    }
    window.localStorage.setItem('egcf_favorite_exhibitor_list', JSON.stringify(favoritesList));
    exhibitor.reload();
    if(this.activeLetterFilter === 'favorites'){
      this.onFilterChanged()
    }
  }

  private hasFavorites(): boolean{
    let favoritesString = window.localStorage.getItem('egcf_favorite_exhibitor_list');
    let favoritesList;
    if (!isDefined(favoritesString)) {
      return false
    } else {
      favoritesList = JSON.parse(favoritesString)
    }
    return favoritesList.length > 0;
  }

  private isFavorite(exhibitor: Exhibitor): boolean {
    let favoritesString = window.localStorage.getItem('egcf_favorite_exhibitor_list');
    let favoritesList;
    if (!isDefined(favoritesString)) {
      return false
    } else {
      favoritesList = JSON.parse(favoritesString)
    }
    return favoritesList.indexOf(exhibitor.id) !== -1;
  }

}
